/**
 * Flipmail app themes. Persists to localStorage under flipmail-theme.
 * Six palettes: three dark, three light.
 */

const accentMuted = (hex, alpha = 0.08) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

/** Solid accent color with alpha (e.g. stacked bar “cold” segment). */
export const hexToRgba = (hex, alpha) => {
  if (!hex || !hex.startsWith("#")) return hex;
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const t = (
  name,
  description,
  mode,
  main,
  secondary,
  surface,
  panel,
  border,
  borderBright,
  text,
  muted,
  dim,
  accent
) => ({
  name,
  description,
  mode,
  main,
  secondary,
  surface,
  panel,
  border,
  borderBright,
  text,
  muted,
  dim,
  accent,
  accentMuted: accentMuted(accent),
  panelGlow: accentMuted(accent, 0.12),
  primary: accent,
  onBackground: text,
  accentGlowShadow: `0 0 24px ${accentMuted(accent, 0.12)}`,
});

/** Three dark, then three light. */
export const themes = {
  rebel_ledger: t(
    "Rebel Ledger",
    "Editorial dark, neon chartreuse punch.",
    "dark",
    "#07090C",
    "#0D1017",
    "#0D1017",
    "#131824",
    "#1C2030",
    "#252C42",
    "#ECEAE0",
    "#7E8695",
    "#434A5C",
    "#C8F73A"
  ),
  midnight_wire: t(
    "Midnight Wire",
    "Deep navy panels, ice cyan signal.",
    "dark",
    "#060814",
    "#0a1020",
    "#0a1020",
    "#101a32",
    "#1a2540",
    "#2a3a58",
    "#E8F0FF",
    "#7d8fb3",
    "#4a5878",
    "#5CE1FF"
  ),
  chrome_noir: t(
    "Chrome Noir",
    "Gunmetal gradients, electric blue highlights.",
    "dark",
    "#0c0d10",
    "#12141a",
    "#12141a",
    "#1a1d26",
    "#252a36",
    "#343b4a",
    "#e6e8ed",
    "#8b93a3",
    "#555c6d",
    "#6EB5FF"
  ),
  paper_teal: t(
    "Paper Teal",
    "Light paper panels, near-black type, deep teal for actions and focus.",
    "light",
    "#f3f4f0",
    "#ebede6",
    "#ebede6",
    "#e3e6dd",
    "#cfd6c8",
    "#b8c2b0",
    "#0c0f0a",
    "#4a5248",
    "#6b7368",
    "#0f766e"
  ),
  paper_sunrise: t(
    "Paper Sunrise",
    "Warm cream surfaces, ember orange actions.",
    "light",
    "#faf6ef",
    "#f2ebe0",
    "#f2ebe0",
    "#e8dfd2",
    "#d4c9b8",
    "#c4b8a6",
    "#1c1917",
    "#6b5d52",
    "#8a7d72",
    "#ea580c"
  ),
  mist_blueprint: t(
    "Mist Blueprint",
    "Cool paper, slate type, cobalt accents.",
    "light",
    "#f8fafc",
    "#f1f5f9",
    "#f1f5f9",
    "#e2e8f0",
    "#cbd5e1",
    "#94a3b8",
    "#0f172a",
    "#475569",
    "#64748b",
    "#2563eb"
  ),
};

const STORAGE_KEY = "flipmail-theme";

/** Map removed theme ids so saved preferences keep working. */
const LEGACY_THEME_MAP = {
  polar_ops: "paper_teal",
  chartreuse_canvas: "paper_teal",
  solar_ash: "rebel_ledger",
  velvet_signal: "midnight_wire",
};

export const getStoredTheme = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && themes[stored]) return stored;
    if (stored && LEGACY_THEME_MAP[stored]) return LEGACY_THEME_MAP[stored];
    return "rebel_ledger";
  } catch {
    return "rebel_ledger";
  }
};

export const saveTheme = (themeName) => {
  try {
    localStorage.setItem(STORAGE_KEY, themeName);
  } catch (e) {
    console.error("Failed to save theme:", e);
  }
};

export const applyTheme = (themeName) => {
  const theme = themes[themeName];
  if (!theme) return;

  const root = document.documentElement;
  root.style.setProperty("--flipmail-color-main", theme.main);
  root.style.setProperty("--flipmail-color-secondary", theme.secondary);
  root.style.setProperty("--flipmail-color-surface", theme.surface);
  root.style.setProperty("--flipmail-color-panel", theme.panel);
  root.style.setProperty("--flipmail-color-border", theme.border);
  root.style.setProperty("--flipmail-color-border-bright", theme.borderBright);
  root.style.setProperty("--flipmail-color-text", theme.text);
  root.style.setProperty("--flipmail-color-muted", theme.muted);
  root.style.setProperty("--flipmail-color-dim", theme.dim);
  root.style.setProperty("--flipmail-color-accent", theme.accent);
  root.style.setProperty("--flipmail-color-accent-muted", theme.accentMuted);
  root.style.setProperty("--flipmail-color-panel-glow", theme.panelGlow);
  root.style.setProperty("--flipmail-color-primary", theme.primary);
  root.style.setProperty("--flipmail-color-on-background", theme.onBackground);
  root.style.setProperty("--flipmail-shadow-accent-glow", theme.accentGlowShadow);
  root.setAttribute("data-flipmail-theme", themeName);
  root.setAttribute("data-flipmail-theme-mode", theme.mode);
};
